import { useContext, useLayoutEffect, type ReactNode } from 'react';
import { AppShell } from './shell';
import { ShellHostCtx } from './shell/shell-host-context';

/**
 * What a page renders around its content to sit in the app shell.
 *
 * ── Inside the persistent shell (every /dashboard route) ────
 * `ShellHost` has already mounted the shell around the routes, so this
 * renders the page's content and nothing else. The only thing it still
 * carries is `fullHeight`, handed up to the shell before paint (a layout
 * effect), so a split-pane queue never shows one frame with page padding.
 *
 * ── Anywhere else ───────────────────────────────────────────
 * Dev previews and error fallbacks rendered outside the host still get a
 * shell of their own, as every page used to.
 */
export interface DashboardLayoutProps {
  children: ReactNode;
  /** Full-height pages (split-pane queues) opt out of main's padding. */
  fullHeight?: boolean;
}

export const DashboardLayout = ({
  children,
  fullHeight = false,
}: DashboardLayoutProps) => {
  const host = useContext(ShellHostCtx);
  const setFullHeight = host?.setFullHeight;

  useLayoutEffect(() => {
    if (!setFullHeight) return;
    setFullHeight(fullHeight);
    return () => setFullHeight(false);
  }, [setFullHeight, fullHeight]);

  if (host) return <>{children}</>;
  return <AppShell fullHeight={fullHeight}>{children}</AppShell>;
};
